import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import Bolt from "@mui/icons-material/Bolt";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { NodeKey } from "lexical";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "../../../../../styles/icons/chevron-down";
import { ChevronUp } from "../../../../../styles/icons/chevron-up";
import { useDialog } from "../../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../../utils/uuid";
import { useLexicalContext } from "../../lexical-context";
import { useLexicalDynamicContentContext } from "../../lexical-dynamic-content-context";
import {
  CLONE_DYNAMIC_CONTENT_COMMAND,
  DELETE_NODE_COMMAND,
  SET_DYNAMIC_CONTENT_OPEN_COMMAND,
} from "../../types/commands";

export const DynamicContentToolbar: FC<{
  segmentId: UUID;
  open: boolean;
  parentKey: NodeKey;
}> = ({ segmentId, open, parentKey }) => {
  const { t } = useTranslation();

  const [editor] = useLexicalComposerContext();

  const {
    disabled,
    editorModal: { handleOpen },
  } = useLexicalContext();

  const {
    segmentCounts: { data: segmentCounts, loading },
  } = useLexicalDynamicContentContext();

  const onChange = useCallback(() => {
    handleOpen({
      type: "dynamic-content",
      data: { mode: "edit", segmentId, key: parentKey },
    });
  }, [handleOpen, segmentId, parentKey]);

  const onClone = useCallback(() => {
    editor.dispatchCommand(CLONE_DYNAMIC_CONTENT_COMMAND, parentKey);
  }, [editor, parentKey]);

  const onToggleOpen = useCallback(() => {
    editor.dispatchCommand(SET_DYNAMIC_CONTENT_OPEN_COMMAND, {
      key: parentKey,
      open: !open,
    });
  }, [editor, parentKey, open]);

  const segmentInfo = segmentCounts?.[segmentId];

  return (
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} width={"100%"}>
      <Stack
        direction="row"
        alignItems="center"
        p={1}
        m={1}
        borderRadius={1}
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        })}
        spacing={1}
      >
        <Tooltip title={t("Dynamic content")}>
          <Bolt fontSize="small" sx={(theme) => ({ color: theme.palette.primary.main })} />
        </Tooltip>
        {loading ? (
          <Skeleton
            variant="text"
            width={100}
            sx={(theme) => ({ color: theme.palette.primary.main })}
          />
        ) : (
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.palette.primary.main, pb: 0 })}
          >
            {segmentInfo?.name ?? t("Unnamed segment")}
          </Typography>
        )}
        {loading ? (
          <Skeleton
            variant="text"
            width={20}
            sx={(theme) => ({ color: theme.palette.primary.main })}
          />
        ) : (
          <Tooltip
            title={t("Number of recipients in both this segment and the Communication segment")}
          >
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.primary.main, pb: 0 })}
            >
              ({segmentInfo?.count ?? 0})
            </Typography>
          </Tooltip>
        )}
        <Tooltip title={t("Change targeting")}>
          <Button
            variant="text"
            size="small"
            onClick={onChange}
            data-analytics-id="edit-dynamic-content"
            disabled={disabled}
            sx={(theme) => ({ color: theme.palette.primary.main })}
          >
            {t("Change")}
          </Button>
        </Tooltip>

        <DeleteButton parentKey={parentKey} />
        <Tooltip title={t("Clone dynamic content block")}>
          <IconButton
            size="small"
            onClick={onClone}
            data-analytics-id="clone-dynamic-content"
            color="inherit"
            disabled={disabled}
            sx={(theme) => ({ color: theme.palette.primary.main })}
          >
            <ContentCopyOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={open ? t("Hide") : t("Show")}>
          <IconButton
            size="small"
            onClick={onToggleOpen}
            data-analytics-id="toggle-dynamic-content"
            sx={(theme) => ({ color: theme.palette.primary.main })}
          >
            {open ? <ChevronUp fontSize="small" /> : <ChevronDown fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

const DeleteButton: FC<{ parentKey: NodeKey }> = ({ parentKey }) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const { open, handleOpen, handleClose } = useDialog();

  const onDelete = useCallback(() => {
    handleClose();
    editor.dispatchCommand(DELETE_NODE_COMMAND, parentKey);
  }, [editor, parentKey, handleClose]);

  return (
    <>
      <Tooltip title={t("Delete dynamic content block")}>
        <IconButton
          size="small"
          onClick={handleOpen}
          data-analytics-id="delete-dynamic-content"
          sx={(theme) => ({ color: theme.palette.primary.main })}
          disabled={false}
        >
          <DeleteOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t("Delete dynamic content?")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              "Deleting this content means that recipients may not see content targeted to their segment, and your content may not make sense any more.",
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
            variant="text"
            data-analytics-id="cancel-dynamic-content-delete"
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={onDelete}
            color="error"
            data-analytics-id="confirm-dynamic-content-delete"
            variant="contained"
          >
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
